/**
 * DataTableColumnHeader Component
 * 
 * Sortable column header with sort icons.
 * Supports keyboard navigation for accessibility.
 */

import { type Column } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { SortArrowsIcon } from '../icons';
import { TABLE_CELL_CLASSES } from './constants';

interface DataTableColumnHeaderProps<TData, TValue> {
  readonly column: Column<TData, TValue>;
  readonly title: string;
  /** Whether this column is sortable (default: true) */
  readonly sortable?: boolean;
  readonly className?: string;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  sortable = true,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const canSort = sortable && column.getCanSort();
  
  if (!canSort) {
    return <span className={className}>{title}</span>;
  }

  const sortDirection = column.getIsSorted();
  const nextSortDirection = sortDirection === 'asc' ? 'descending' : sortDirection === 'desc' ? 'default' : 'ascending';

  const handleSort = column.getToggleSortingHandler();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSort?.(event);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Sort by ${title}, ${nextSortDirection}`}
      className={cn(
        'flex items-center justify-between gap-4 cursor-pointer select-none',
        'hover:bg-dinnerplate-white-light',
        `-mx-6 -my-4 ${TABLE_CELL_CLASSES}`,
        'focus:outline-none',
        className
      )}
      onClick={handleSort}
      onKeyDown={handleKeyDown}
    >
      <span>{title}</span>
      <SortArrowsIcon
        activeDirection={sortDirection || null}
        className="w-2.5 shrink-0"
        aria-hidden="true"
      />
    </div>
  );
}
